import { Components, Theme } from '@mui/material';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    displayLG: true;
    displayMD: true;
    displaySM: true;
    headlineLG: true;
    headlineMD: true;
    headlineSM: true;
    titleLG: true;
    titleMD: true;
    titleSM: true;
    bodyLG: true;
    bodyMD: true;
    bodySM: true;
    labelLG: true;
    labelMD: true;
    labelSM: true;
  }
}

const variantMap = {
  /** Large display variant with a font size of 57px and line height of 64px. */
  displayLG: {
    fontWeight: 400,
    fontSize: '57px',
    lineHeight: '64px',
  },
  /** Medium display variant with a font size of 45px and line height of 52px. */
  displayMD: {
    fontWeight: 400,
    fontSize: '45px',
    lineHeight: '52px',
  },
  /** Small display variant with a font size of 36px and line height of 44px. */
  displaySM: {
    fontWeight: 400,
    fontSize: '36px',
    lineHeight: '44px',
  },
  /** Large headline variant with a font size of 32px and line height of 40px. */
  headlineLG: {
    fontWeight: 400,
    fontSize: '32px',
    lineHeight: '40px',
  },
  /** Medium headline variant with a font size of 28px and line height of 36px. */
  headlineMD: {
    fontWeight: 400,
    fontSize: '28px',
    lineHeight: '36px',
  },
  /** Small headline variant with a font size of 24px and line height of 32px., and letter spacing of 0px. */
  headlineSM: {
    fontWeight: 400,
    fontSize: '24px',
    lineHeight: '32px',
    letterSpacing: '0px',
  },
  /** Large title variant with a font size of 22px, line height of 28px, and letter spacing of 0.15px. */
  titleLG: {
    fontWeight: 400,
    fontSize: '22px',
    lineHeight: '28px',
    letterSpacing: '0.15px',
  },
  /** Medium title variant with a font size of 16px, line height of 24px, and letter spacing of 0.15px. */
  titleMD: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  /** Small title variant with a font size of 14px, line height of 20px, and letter spacing of 0.15px. */
  titleSM: {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.15px',
  },
  /** Large label variant with a font size of 14px and line height of 20px, and letter spacing of 0.1px. */
  labelLG: {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
  },
  /** Medium label variant with a font size of 12px and line height of 16px, and letter spacing of 0.5px. */
  labelMD: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.5px',
  },
  /** Small label variant with a font size of 11px and line height of 16px, and letter spacing of 0.5px. */
  labelSM: {
    fontWeight: 500,
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.5px',
  },
  /** Large body text variant with a font size of 16px and line height of 24px. */
  bodyLG: {
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
  },
  /** Medium body text variant with a font size of 14px and line height of 20px, and letter spacing of 0.25px. */
  bodyMD: {
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.25px',
  },
  /** Small body text variant with a font size of 12px and line height of 16px, and letter spacing of 0.4px. */
  bodySM: {
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.4px',
  },
};

export const MuiTypography: Components<
  Omit<Theme, 'components'>
>['MuiTypography'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      letterSpacing: '0.15px',
      color: theme.palette.text.primary,
      fontSize: '14px',
      variants: Object.entries(variantMap).map(([variant, style]) => ({
        props: { variant },
        style: { ...style, display: 'block' },
      })),
    }),
  },
};
